import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import HeaderDetails from "@shared/components/application/data-display/HeaderDetails/HeaderDetails.web";
import ItemQuantitySelectInfo from "@shared/components/application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import DetailCardContainer from "@shared/components/ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import { enums } from "@shared/constants";
import useMediaQuery from "@shared/hooks/useMediaQuery/useMediaQuery";
import useWindowSize from "@shared/hooks/useWindowSize";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import breakpoints from "@shared/theme/props/breakpoints";
import { getEventImageSource } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import BaseTabs from "@ui-kit/disclosure/BaseTabs/BaseTabs.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { useMemo, useRef, useState } from "react";
import { AnalyticsService } from "@shared/services/analytics";
import CustomerReviews from "@app-contexts/public/features/event-detail/components/CustomerReviews/CustomerReviews.web";
import {
  useCustomerReviewsData,
  UseCustomerReviewsDataResult,
} from "@app-contexts/public/features/event-detail/hooks/useCustomerReviewsData";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useEventRatings } from "@app-contexts/public/features/event-detail/hooks/useEventRatings";
import EventBadges from "../event-details/EventBadges/EventBadges.web";
import { EventsModalProps } from "./EventsModal.props";
import { TicketTypesListProps } from "../tickets/TicketTypesList/TicketTypesList.props";

const { sm } = breakpoints;

const EventImage = ({ event }: { event: PublicBaseEventSerializer }) => {
  return (
    <Box mx="auto" width={{ base: "100%", sm: "element.13xl" }}>
      <CardImage
        src={getEventImageSource(event)}
        alt={`Event image for ${event?.name}`}
        height={600}
        width={1200}
        borderRadius={{ base: "none", sm: "md" }}
      />
    </Box>
  );
};

const DescriptionPanel = ({
  description,
}: {
  description: string | undefined;
}) => {
  const { t } = useTranslation("purchases");
  return (
    <Flex borderRadius="sm" bg="bg" p="xl">
      <ParsedHtml htmlString={description || t("No description available.")} />
    </Flex>
  );
};

const Skeleton = (): JSX.Element => {
  return (
    <DetailCardContainer>
      <ItemQuantitySelectInfo
        currency={enums.CURRENCY_MAP.CAD.id}
        isLoading
        amountSelected={0}
        onAmountSelectedChange={undefined}
        isWaitlistTicket
      />
    </DetailCardContainer>
  );
};

const Tabs = ({
  event,
  childEvent,
  isLoading = false,
  ticketTypesListProps,
  hideDescription,
  hideBackButton,
  enableAffirm,
  tabIndex,
  onTabChange,
  reviewsData,
  showDistribution,
}: {
  event: PublicBaseEventSerializer | undefined;
  childEvent?: PublicBaseEventSerializer;
  isLoading: boolean;
  ticketTypesListProps?: TicketTypesListProps;
  hideDescription?: boolean;
  hideBackButton?: boolean;
  enableAffirm?: boolean;
  tabIndex: number;
  onTabChange: (index: number) => void;
  reviewsData: UseCustomerReviewsDataResult | null;
  showDistribution: boolean;
}) => {
  const { t } = useTranslation("purchases");

  const panelRefs = useRef<HTMLDivElement | null>(null);
  /**
   * This is a handler to calculate the minHeight of the tabs container.
   * The minHeight is calculated based on the first tab that holds the items
   */
  const minHeight = useMemo(() => {
    if (panelRefs.current) {
      /*
       * If the panel height is greater than 600px, we set the minHeight
       *  to 50vh to prevent overlay and scroll issues
       */
      if (panelRefs.current.scrollHeight > 600) return "40vh";
      return panelRefs.current.scrollHeight;
    }
    return "auto";
  }, [panelRefs.current]);

  const hasDescription = Boolean(event?.description) && !hideDescription;
  // Show reviews tab if we have valid reviews data
  const hasReviews = Boolean(reviewsData);

  return (
    <BaseTabs
      isFitted
      display="flex"
      flexDirection="column"
      flex="1"
      minHeight="0"
      id="event-modal-tabs"
      data-testid="event-modal-tabs"
      index={tabIndex}
      onChange={onTabChange}
    >
      <BaseTabs.TabList
        bg="bg"
        position="sticky"
        top="none"
        zIndex="1"
        flexShrink={0}
        // Only show tab list if there are multiple tabs to navigate between
        display={hasDescription || hasReviews ? "flex" : "none"}
      >
        <BaseTabs.Tab
          padding="4px 0"
          data-testid="event-modal-buy-tickets-tab-button"
        >
          {t("Buy tickets")}
        </BaseTabs.Tab>
        {hasDescription && (
          <BaseTabs.Tab
            padding="4px 0"
            data-testid="event-modal-description-tab-button"
          >
            {t("Description")}
          </BaseTabs.Tab>
        )}
        {hasReviews && (
          <BaseTabs.Tab
            padding="4px 0"
            data-testid="event-modal-reviews-tab-button"
          >
            {t("Reviews")}
          </BaseTabs.Tab>
        )}
      </BaseTabs.TabList>
      <BaseTabs.TabPanels flex="1" minHeight={minHeight}>
        <BaseTabs.TabPanel ref={panelRefs} padding={{ base: "lg", sm: "xl" }}>
          {isLoading ? (
            <>
              {Array.from(
                { length: event?.ticket_types?.length ?? 1 },
                (_, i) => (
                  <Skeleton key={i} />
                )
              )}
            </>
          ) : (
            <TicketTypesList
              enableAffirm={enableAffirm}
              eventData={event}
              childEvent={childEvent}
              hideBackButton={hideBackButton}
              {...ticketTypesListProps}
            />
          )}
        </BaseTabs.TabPanel>
        {hasDescription && (
          <BaseTabs.TabPanel
            padding={{ base: "lg", sm: "xl" }}
            data-testid="event-modal-description-panel"
          >
            <DescriptionPanel description={event?.description} />
          </BaseTabs.TabPanel>
        )}
        {hasReviews && reviewsData && (
          <BaseTabs.TabPanel
            padding={{ base: "lg", sm: "xl" }}
            data-testid="event-modal-reviews-panel"
          >
            <CustomerReviews
              overallRating={reviewsData.overallRating}
              reviews={reviewsData.reviews}
              isLoading={reviewsData.isLoading}
              hasNoResults={reviewsData.hasNoResults}
              hasNextPage={reviewsData.hasNextPage}
              isFetchingNextPage={reviewsData.isFetchingNextPage}
              onLoadMore={reviewsData.fetchNextPage}
              activeStarRating={reviewsData.activeStarRating}
              onStarRatingChange={reviewsData.setActiveStarRating}
              showDistribution={showDistribution}
            />
          </BaseTabs.TabPanel>
        )}
      </BaseTabs.TabPanels>
    </BaseTabs>
  );
};

const EventsModal = ({
  isOpen,
  onClose,
  event,
  isTicketTypesLoading = false,
  isLoading = false,
  modalHeight,
  analyticsSource,
}: EventsModalProps) => {
  const { isBasketUpdating } = usePublicBasket();

  const { t } = useTranslation("purchases");

  const handleContinue = () => {
    if (analyticsSource) {
      AnalyticsService.buttonClick({
        label: "addons",
        custom_params: {
          datum: "add_to_cart",
          type: "event",
          id: event.id,
          source: analyticsSource,
        },
      });
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      isCentered
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
      modalContentProps={
        modalHeight ? { maxH: modalHeight, minH: modalHeight } : undefined
      }
      variant="interactive"
    >
      <Modal.Header border="none">
        <Flex width="100%" justify="space-between" align="center">
          {t("Select tickets")}
          <Box flexShrink={0} mr="md">
            <CartTimer.Checkout />
          </Box>
        </Flex>
      </Modal.Header>

      <Modal.Body id="events-modal-body" padding="none" overflowY="auto">
        <EventsModal.Content
          event={event}
          isLoading={isLoading}
          isTicketTypesLoading={isTicketTypesLoading}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button.Solid
          data-testid="event-modal-continue-button"
          onClick={handleContinue}
          disabled={isBasketUpdating}
        >
          {t("Continue", { ns: "common" })}
        </Button.Solid>
        <Button.Ghost
          onClick={onClose}
          data-testid="event-modal-cancel-button"
          disabled={isBasketUpdating}
        >
          {t("Cancel", { ns: "common" })}
        </Button.Ghost>
      </Modal.Footer>
    </Modal>
  );
};

EventsModal.Badge = function EventModalBadge({
  badges,
}: {
  badges: Array<{
    id: number;
    icon: JSX.Element;
    text: JSX.Element;
    showBadge: boolean;
    dataTestId: string;
  }>;
}) {
  const [isSmall] = useMediaQuery(`(max-width: ${sm - 1}px)`);

  return isSmall ? (
    <Flex
      mt="xs"
      fontSize="xs"
      display="inline-block"
      hidden={!badges.find((badge) => badge.showBadge)}
    >
      {badges.map(
        ({ id, showBadge, dataTestId, icon, text }) =>
          showBadge && (
            <Flex
              key={id}
              p="5px 0px"
              alignItems="center"
              data-testid={dataTestId}
            >
              <Box display="flex" alignItems="center" mr="2xs">
                {icon}
              </Box>
              {text}
            </Flex>
          )
      )}
    </Flex>
  ) : (
    <SimpleGrid
      mt="xs"
      columns={2}
      fontSize="sm"
      hidden={!badges.find((badge) => badge.showBadge)}
    >
      {badges.map(
        ({ id, showBadge, dataTestId, icon, text }) =>
          showBadge && (
            <Flex
              key={id}
              mr="lg"
              p="0px 0px 8px 0px"
              alignItems="center"
              data-testid={dataTestId}
              fontSize="xs"
            >
              <Box display="flex" alignItems="center" mr="2xs">
                {icon}
              </Box>
              {text}
            </Flex>
          )
      )}
    </SimpleGrid>
  );
};

EventsModal.Content = function EventModalContent({
  event,
  childEvent,
  isLoading,
  isTicketTypesLoading,
  ticketTypesListProps,
  hideDescription,
  hideBackButton,
  enableAffirm,
}: {
  event: PublicBaseEventSerializer;
  childEvent?: PublicBaseEventSerializer;
  isLoading: boolean;
  isTicketTypesLoading: boolean;
  ticketTypesListProps?: TicketTypesListProps;
  hideDescription?: boolean;
  hideBackButton?: boolean;
  enableAffirm?: boolean;
}) {
  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => Boolean(windowWidth && windowWidth < sm - 1),
    [windowWidth]
  );

  const { isFlagActive } = useDjangoWaffle();

  const venueId = event?.venue?.id;
  const { isCustomerReviewsEnabled, rating } = useEventRatings({
    eventRating: event?.rating,
    venueRating: event?.venue?.rating,
    venueId,
  });
  const reviewsData = useCustomerReviewsData({
    venueId,
    venueRating: event?.venue?.rating,
    eventId: event?.id,
    eventRating: event?.rating,
    enabled: isCustomerReviewsEnabled,
  });
  const showDistribution = isFlagActive(
    "show_review_rating_distribution",
    venueId
  );

  const hasDescription = Boolean(event?.description) && !hideDescription;
  // Calculate the reviews tab index dynamically based on whether description tab exists
  // Tab 0: Tickets (always present)
  // Tab 1: Description (if hasDescription) or Reviews (if no description)
  // Tab 2: Reviews (if hasDescription)
  const reviewsTabIndex = hasDescription ? 2 : 1;

  const [tabIndex, setTabIndex] = useState(0);

  const handleClickRating = () => {
    setTabIndex(reviewsTabIndex);
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  return (
    <Flex direction="column" gap="none" h="100%" id="event-modal-content">
      <Box
        padding={{ base: "none", sm: "0 24px" }}
        backgroundColor="bg"
        flexShrink={0}
      >
        <HeaderDetails
          isTablet={false}
          // We will always have event data at this point, since it's being passed in as a prop.
          isLoading={isLoading}
          title={event?.name}
          saleStartsOn={event?.event_sale_starts_on}
          timezone={event?.timezone}
          organizer={event?.venue}
          subtitle={event?.subtitle}
          isSmallerThanSm={isSmallerThanSm}
          isModal
          imageElement={<EventsModal.Image event={event} />}
          badgeElement={
            <EventsModal.Badge
              badges={EventBadges({
                isSmallerThanSm: Boolean(isSmallerThanSm),
                eventData: event,
                condensed: true,
              })}
            />
          }
          fontSize="small"
          rating={rating}
          onClickRating={reviewsData ? handleClickRating : undefined}
          columnGap="element.xl"
        />
      </Box>
      <Tabs
        event={event}
        childEvent={childEvent}
        isLoading={isTicketTypesLoading || isLoading}
        ticketTypesListProps={ticketTypesListProps}
        hideDescription={hideDescription}
        hideBackButton={hideBackButton}
        enableAffirm={enableAffirm}
        tabIndex={tabIndex}
        onTabChange={handleTabChange}
        reviewsData={reviewsData}
        showDistribution={showDistribution}
      />
    </Flex>
  );
};

EventsModal.Tabs = Tabs;
EventsModal.Image = EventImage;

export default EventsModal;
